import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function CrmLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  // If already logged in, jump straight to the right dashboard for the role.
  useEffect(() => {
    if (loading || !user) return;
    if (user.is_coordinator || user.is_admin || user.is_org_admin) {
      navigate(params.get("next") || "/crm");
    } else if (user.is_teacher) {
      navigate("/teacher");
    } else if (user.is_section_leader) {
      navigate("/attendance/section");
    }
  }, [user, loading, navigate, params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      // Route teachers to the teacher portal instead of rejecting them.
      if (u.is_teacher && !u.is_coordinator && !u.is_admin && !u.is_org_admin) {
        toast.success(`Welcome, ${u.name?.split(" ")[0] || "teacher"}. Taking you to the teacher portal.`);
        navigate("/teacher");
        return;
      }
      if (u.is_section_leader && !u.is_coordinator && !u.is_admin && !u.is_org_admin) {
        toast.success(`Welcome, ${u.name?.split(" ")[0] || "friend"}. Opening your section.`);
        navigate("/attendance/section");
        return;
      }
      if (!u.is_coordinator && !u.is_admin && !u.is_org_admin) {
        logout();
        toast.error("This account isn't set up as a coordinator. Ask your site admin for access.");
        return;
      }
      toast.success(`Welcome, ${u.name?.split(" ")[0] || "coordinator"}.`);
      navigate(params.get("next") || "/crm");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="crm-login-page">
      <div className="overline mb-3">Coordinator sign-in</div>
      <h1 className="font-serif-display text-4xl tracking-tight" style={{ color: "var(--ink)" }}>Sign in to the CRM</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
        Use the credentials your site admin sent you.
      </p>
      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <label className="block text-xs mb-2 overline">Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="crm-login-email"
            className="w-full bg-white border px-4 py-3 outline-none focus:border-[var(--terracotta)]"
            style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }}
          />
        </div>
        <div>
          <label className="block text-xs mb-2 overline">Password</label>
          <input
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="crm-login-password"
            className="w-full bg-white border px-4 py-3 outline-none focus:border-[var(--terracotta)]"
            style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }}
          />
        </div>
        <button type="submit" disabled={busy} data-testid="crm-login-submit" className="btn-primary w-full disabled:opacity-50">
          <LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-xs" style={{ color: "var(--muted)" }}>
        Forgot your password? Contact your site admin — they can reset it from the admin dashboard.
      </p>
      <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
        Home study teachers can sign in with the same email here — we'll take you to your teacher portal automatically, or head straight to <a href="/teacher/login" className="hover:underline" style={{ color: "var(--terracotta)" }}>the teacher sign-in page</a>.
      </p>
    </div>
  );
}
